import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { useUser } from "@saleor/auth";
import { Zone } from "@saleor/auth/types";
import { FIELD_TYPE } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  InquiryOrderConfirmationStatus,
  useDistributionChannelsDomesticQuery,
  useDistributionChannelsExportQuery,
  useDropdownListMaterialGroup1Query,
  useFilterDomesticOrderConfirmationCompanyQuery,
  useFilterRequireAttentionMaterialLazyQuery,
  useScgpMeQuery,
  useSearchSuggestionDomesticSoldTosLazyQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getProductGroup } from "./utils";

const useOrderFilterConfirmation = ({ handleChangeFilter }) => {
  const intl = useIntl();
  const { zone } = useUser();
  const { data: domesticChannels } = useDistributionChannelsDomesticQuery();
  const { data: exportChannels } = useDistributionChannelsExportQuery();
  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });

  const channels = useMemo(
    () =>
      (zone === Zone.Export
        ? exportChannels?.distributionChannelsExport
        : domesticChannels?.distributionChannelsDomestic) || [],
    [exportChannels, domesticChannels, zone]
  );

  const [{ filterOrderConfirm: filterAppState }] = useAppState();

  const initChannel = useMemo(() => channels[0], [channels]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: filterAppState?.soldTo || [],
      channel: filterAppState?.distributionChannel || `${initChannel?.code}`,
      productGroup: filterAppState?.materialGroup,
      orderCreateDate: {
        gte: filterAppState?.orderCreateDate?.gte || "",
        lte: filterAppState?.orderCreateDate?.lte || ""
      },
      poNo: filterAppState?.poNo || "",
      bu: filterAppState?.bu || "",
      saleOrganization: filterAppState?.saleOrganization || "",
      soNo: filterAppState?.soNo || "",
      material: filterAppState?.material || [],
      status: filterAppState?.status || "ALL"
    },
    onSubmit: () => {
      const errorFields = [];
      if (errorFields.length) {
        errorFields.forEach(e => {
          if (!e.value) {
            formik.setFieldError(e.fieldName, e.error);
          }
        });
      } else {
        handleChangeFilter(formik.values);
      }
    }
  });

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const {
    data: confirmationConpanies
  } = useFilterDomesticOrderConfirmationCompanyQuery();

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    handleChangeFilter({});
    setTimeout(() => {
      setIsClean(false);
      formik.resetForm();
    }, 500);
  }, []);

  const [
    getMaterialCustomerOrder,
    { data: fetchMaterialCustomerOrder, loading: loadingMaterialCustomerOrder }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const [
    getMaterialAdminOrder,
    { data: fetchMaterialAdminOrder, loading: loadingMaterialAdminOrder }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useSearchSuggestionDomesticSoldTosLazyQuery();
  const productGroupList = useMemo(() => {
    if (formik.values.bu === "CIP") {
      formik.setFieldValue("productGroup", "");
      return [];
    }
    formik.setFieldValue("productGroup", "All");
    return getProductGroup(
      productGroup,
      intl.formatMessage({ defaultMessage: "All" })
    );
  }, [formik.values.bu, productGroup]);
  const { data: scgpMe } = useScgpMeQuery({
    displayLoader: true
  });
  const buList = useMemo(
    () =>
      scgpMe?.scgpMe?.extendData?.scgpBus?.map(bu => ({
        label: bu.name,
        value: bu.name
      })),
    [scgpMe?.scgpMe?.extendData?.scgpBus]
  );

  const saleOrgList = useMemo(() => {
    const orgList =
      scgpMe?.scgpMe?.extendData?.salesOrgByBu
        ?.find(org => org?.businessUnit === formik?.values?.bu)
        ?.salesOrganizations?.map(e => ({
          label: `${e.code} - ${e.shortName || ""}`,
          value: e.code
        })) || [];
    return orgList;
  }, [scgpMe?.scgpMe?.extendData?.salesOrgByBu, formik?.values?.bu]);

  useEffect(() => {
    const extendData = scgpMe?.scgpMe?.extendData;

    const bu = formik?.values?.bu || extendData?.defaultBusinessUnit?.name;

    const salesOrgByBuAndUser =
      extendData?.salesOrgByBuAndUser?.find(org => org?.businessUnit === bu)
        ?.salesOrganizations || [];

    formik.setValues(prev => ({
      ...prev,
      bu,
      saleOrganization:
        salesOrgByBuAndUser?.length === 1 ? salesOrgByBuAndUser[0].code : ""
    }));
  }, [
    scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name,
    formik?.values?.bu
  ]);

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold to"
        }),
        subTitle: "(เลือกค้นหา Sold-to ได้ไม่เกิน 10 รายการ)",
        name: "soldTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sold to..."
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT,
        disabled: false,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: " Material Code / Description"
        }),
        subTitle: "(เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "material",
        placeholder: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT,
        isShowSelected: false
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Create Date" }),
        name: "orderCreateDate",
        required: !formik.values.soNo && !formik.values.poNo,
        type: FIELD_TYPE.DATEPICKER
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก BU"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: buList || [],
        md: 2,
        sm: 6
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Organization" }),
        name: "saleOrganization",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sales Org."
        }),
        required: !formik.values.soNo && !formik.values.poNo,
        type: FIELD_TYPE.SINGLE_SELECT,
        options: saleOrgList,
        md: 2,
        sm: 6
      },
      {
        title: intl.formatMessage({ defaultMessage: "Distribution Channel" }),
        name: "channel",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Channel"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: channels.map(e => ({
          label: `${e.code} - ${e.name}`,
          value: `${e.code}`
        })),
        md: 2,
        sm: 6
      },
      {
        title: intl.formatMessage({ defaultMessage: "Product Group" }),
        name: "productGroup",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Order Type" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: productGroupList,
        disabled: formik.values.bu === "CIP"
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        name: "poNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "SO No."
        }),
        name: "soNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ SO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "Status" }),
        name: "status",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Status" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: [
          {
            label: "All",
            value: InquiryOrderConfirmationStatus.ALL
          },
          {
            label: "พร้อมส่ง",
            value: InquiryOrderConfirmationStatus.CONFIRM
          },
          {
            label: "รอผลิต",
            value: InquiryOrderConfirmationStatus.NON_CONFIRM
          },
          {
            label: "ยกเลิก",
            value: InquiryOrderConfirmationStatus.REJECT
          }
        ],
        maxLength: 10
      }
    ],
    [
      confirmationConpanies,
      channels,
      buList,
      saleOrgList,
      productGroupList,
      formik.values.soNo,
      formik.values.poNo
    ]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getMaterialCustomerOrder,
      fetchMaterialCustomerOrder,
      loadingMaterialCustomerOrder,
      fetchMaterialAdminOrder,
      getMaterialAdminOrder,
      loadingMaterialAdminOrder,
      getSoldToDataSuggestions,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      domesticChannels,
      exportChannels
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getMaterialCustomerOrder,
      fetchMaterialCustomerOrder,
      loadingMaterialCustomerOrder,
      fetchMaterialAdminOrder,
      getMaterialAdminOrder,
      loadingMaterialAdminOrder,
      getSoldToDataSuggestions,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      domesticChannels,
      exportChannels
    ]
  );
};

export default useOrderFilterConfirmation;
